import { type PropertyCard as PropertyCardData } from '@resolute/schema';
import { buttonVariants, cn, Container, Section } from '@resolute/ui';
import { useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useFeaturedProperties } from '../api';

import { Reveal } from '@/components/motion/reveal';
import { PropertyCard } from '@/components/property/property-card';
import { PropertyCardSkeleton } from '@/components/property/property-card-skeleton';

/** Fixed card width for the marquee track — deliberately smaller than the
 *  section's old 3-up grid columns (~450px each on a full-width desktop
 *  viewport), since a horizontally-scrolling row needs an explicit width per
 *  card rather than a fluid grid share. */
const CARD_WIDTH = 'w-72 sm:w-80 lg:w-[22rem]';

export function FeaturedProperties() {
  const { data, isLoading, isError, refetch } = useFeaturedProperties(12);
  const reduce = useReducedMotion();

  return (
    <Section spacing="md">
      <Container>
        <Reveal className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-medium uppercase tracking-widest text-secondary">
              Handpicked
            </p>
            <h2 className="mt-2 font-display text-display-sm font-semibold tracking-tight">
              Featured Inventory
            </h2>
          </div>
          <Link
            to="/buy"
            className={cn(
              buttonVariants({ variant: 'outline', size: 'md' }),
              'group self-start [&:hover_svg]:translate-x-0.5 [&_svg]:transition-transform [&_svg]:duration-base [&_svg]:ease-out-expo',
            )}
          >
            View all listings
            <ArrowRight className="size-4" aria-hidden="true" />
          </Link>
        </Reveal>

        {isError ? (
          <div
            role="alert"
            className="flex flex-col items-center gap-4 rounded-lg border border-border bg-card p-12 text-center"
          >
            <p className="text-muted-foreground">We couldn&apos;t load featured properties.</p>
            <button
              onClick={() => refetch()}
              className={buttonVariants({ variant: 'primary', size: 'sm' })}
            >
              Try again
            </button>
          </div>
        ) : isLoading || !data ? (
          // Mirrors FeaturedMarquee's own wrapper (`overflow-y-visible py-1`)
          // and track spacing exactly. Without the matching `py-1` this row was
          // 8px shorter than the loaded marquee, so the section stepped taller
          // the instant results arrived.
          <div className="overflow-x-hidden overflow-y-visible py-1">
            <div className="flex gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className={cn(CARD_WIDTH, 'shrink-0')}>
                  <PropertyCardSkeleton />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <FeaturedMarquee properties={data} reduce={reduce ?? false} />
        )}
      </Container>
    </Section>
  );
}

/**
 * One-direction, infinite, seamless ticker (never a slide-by-slide carousel,
 * never reverses). The track renders the property list twice back to back;
 * animating exactly -50% always ends on the start of the identical second
 * copy, so the loop has no visible seam or reset jump. Pausing on hover lets
 * someone actually read or click a card without it sliding away mid-hover.
 *
 * Reduced-motion users AND touch/coarse-pointer devices (phones, tablets)
 * get a plain, static, single (non-duplicated) row they can scroll manually
 * instead. The pause mechanism above is `:hover`-only, which never fires on
 * a touch device — without this, a phone user would get an endless
 * 36s-loop ticker they can never pause to actually read or tap a card.
 * Duplicating the content only makes sense alongside the animation; showing
 * it twice while static would just look like a repeated list.
 */
function FeaturedMarquee({
  properties,
  reduce,
}: {
  properties: PropertyCardData[];
  reduce: boolean;
}) {
  const [noHover] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches,
  );
  // Duplicating the track only reads as a loop once there's a real row of
  // cards to scroll through — at 3 or fewer, doubling it just repeats the
  // same handful of cards back to back instead of an actual loop.
  const staticMode = reduce || noHover || properties.length <= 3;
  const track = staticMode ? properties : [...properties, ...properties];

  return (
    // `overflow-x` only, not the shorthand `overflow-hidden` — the latter
    // also clips the Y axis, and the card's rendered height matches this
    // wrapper's height almost exactly (it's sized to its tallest child),
    // so sub-pixel rounding was clipping the card's own bottom border/shadow.
    // Only the infinite horizontal scroll ever needed hiding; a touch of
    // vertical padding gives the hover-lift transform and shadow headroom too.
    <div
      className={cn(
        'group/marquee overflow-x-hidden overflow-y-visible py-1',
        staticMode && 'overflow-x-auto',
      )}
    >
      <div
        className={cn(
          'flex gap-5',
          staticMode
            ? 'px-1'
            : 'w-max animate-marquee will-change-transform group-hover/marquee:[animation-play-state:paused]',
        )}
      >
        {track.map((property, i) => (
          <div key={`${property.id}-${i}`} className={cn(CARD_WIDTH, 'shrink-0')}>
            <PropertyCard property={property} />
          </div>
        ))}
      </div>
    </div>
  );
}
